
import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faCircle,
  faRupeeSign,
  faPlus,
  faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Card, Button } from '@themesberg/react-bootstrap';
import { PageQuoteTable } from "../../components/Tables";
import QuotesTrendChart from "../../components/QuotesTrendChart";
import { Link } from "react-router-dom";
import { Routes } from "../../routes";
import { readAuthSession } from "../../utils/authSession";
import SuperAdminDashboard from "./SuperAdminDashboard";
import PermissionGate from "../../components/PermissionGate";
import { hasUserPermission } from "../../utils/userPermissions";
import axios from "axios";
import { API_URL, END_POINT_MONTH_QUOTES } from "../../const";
import { formatFullInr, formatShortInr } from "../../utils/formatAmount";

const STATUS_CARDS = [
  { key: "live", label: "Live", tone: "live" },
  { key: "won", label: "Won", tone: "won" },
  { key: "closed", label: "Closed", tone: "closed" },
  { key: "lost", label: "Lost", tone: "lost" },
];

const CreateQuoteAction = () => (
  <PermissionGate module="quotes" action="add">
    <Link to={Routes.CreateQuotes.path}>
      <Button variant="primary" className="btn-quote-blue dashboard-cta">
        <FontAwesomeIcon icon={faPlus} className="me-2" />
        Create Quote
      </Button>
    </Link>
  </PermissionGate>
);

const VendorDashboard = () => {
  const [summary, setSummary] = useState({
    totalQuotes: 0,
    totalAmount: 0,
    status: {
      live: 0,
      won: 0,
      closed: 0,
      lost: 0,
    },
    companyAmounts: [],
  });

  useEffect(() => {
    const fetchSummary = async () => {
      const rawToken = sessionStorage.getItem("quotetoken");
      const token = rawToken?.replace(/^"|"$/g, "");

      try {
        const response = await axios.get(API_URL + END_POINT_MONTH_QUOTES, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.status && response.data.data) {
          const data = response.data.data;
          setSummary({
            totalQuotes: data.total_quotes,
            totalAmount: data.total_amount,
            status: {
              live: data.quotes_by_status.live,
              won: data.quotes_by_status.won,
              closed: data.quotes_by_status.closed,
              lost: data.quotes_by_status.lost,
            },
            companyAmounts: data.total_amount_by_company,
          });
        }
      } catch (error) {
        console.error("Error fetching dashboard summary:", error);
      }
    };

    fetchSummary();
  }, []);

  const totalStatusQuotes = Object.values(summary.status).reduce(
    (sum, count) => sum + count,
    0
  );

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <p className="dashboard-eyebrow mb-1">Overview</p>
          <h2 className="dashboard-title mb-1">Dashboard</h2>
          <p className="dashboard-subtitle mb-0">
            Track quote performance, status breakdown, and company totals at a glance.
          </p>
        </div>

        <CreateQuoteAction />
      </div>

      <Row className="g-4 mb-4">
        <Col xs={12} md={6} xl={4}>
          <Card className="dashboard-stat-card dashboard-stat-card--primary h-100">
            <Card.Body>
              <div className="dashboard-stat-icon">
                <FontAwesomeIcon icon={faChartPie} />
              </div>
              <p className="dashboard-stat-label">Total Quotes</p>
              <h3 className="dashboard-stat-value">
                {summary.totalQuotes || totalStatusQuotes}
              </h3>
              <p className="dashboard-stat-meta mb-0">
                Current month summary
              </p>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={6} xl={4}>
          <Card className="dashboard-stat-card dashboard-stat-card--amount h-100">
            <Card.Body>
              <div className="dashboard-stat-icon">
                <FontAwesomeIcon icon={faRupeeSign} />
              </div>
              <p className="dashboard-stat-label">Live Quote Value</p>
              <h3 className="dashboard-stat-value" title={formatFullInr(summary.totalAmount)}>
                {formatShortInr(summary.totalAmount)}
              </h3>
              <p className="dashboard-stat-meta mb-0">
                Live quotes across all companies
              </p>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} xl={4}>
          <Card className="dashboard-stat-card dashboard-stat-card--neutral h-100">
            <Card.Body>
              <p className="dashboard-stat-label mb-3">Quote Status</p>
              <div className="dashboard-status-grid">
                {STATUS_CARDS.map(({ key, label, tone }) => (
                  <div key={key} className={`dashboard-status-pill dashboard-status-pill--${tone}`}>
                    <span className="dashboard-status-pill__label">{label}</span>
                    <span className="dashboard-status-pill__value">{summary.status[key]}</span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4 mb-4">
        <Col xs={12} lg={5} xl={4}>
          <Card className="dashboard-panel h-100">
            <Card.Header className="dashboard-panel__header">
              <div>
                <h5 className="dashboard-panel__title mb-1">Status Breakdown</h5>
                <p className="dashboard-panel__subtitle mb-0">
                  Distribution of quotes by current status
                </p>
              </div>
            </Card.Header>
            <Card.Body className="dashboard-panel__body">
              {STATUS_CARDS.map(({ key, label, tone }) => {
                const count = summary.status[key];
                const percentage = totalStatusQuotes
                  ? Math.round((count / totalStatusQuotes) * 100)
                  : 0;

                return (
                  <div key={key} className="dashboard-progress-item">
                    <div className="dashboard-progress-item__top">
                      <span className={`dashboard-progress-item__label dashboard-status-dot--${tone}`}>
                        <FontAwesomeIcon icon={faCircle} className="me-2" />
                        {label}
                      </span>
                      <span className="dashboard-progress-item__count">{count}</span>
                    </div>
                    <div className="dashboard-progress-track">
                      <div
                        className={`dashboard-progress-fill dashboard-progress-fill--${tone}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={7} xl={8}>
          <Card className="dashboard-panel h-100">
            <Card.Header className="dashboard-panel__header">
              <div>
                <h5 className="dashboard-panel__title mb-1">Amount by Company</h5>
                <p className="dashboard-panel__subtitle mb-0">
                  Company-wise value of Live quotes
                </p>
              </div>
            </Card.Header>
            <Card.Body className="dashboard-panel__body dashboard-company-list">
              {summary.companyAmounts.length > 0 ? (
                summary.companyAmounts.map((item, index) => (
                  <div key={`${item.company_name}-${index}`} className="dashboard-company-item">
                    <div className="dashboard-company-item__info">
                      <span className="dashboard-company-item__rank">{index + 1}</span>
                      <span className="dashboard-company-item__name">{item.company_name}</span>
                    </div>
                    <span className="dashboard-company-item__amount" title={formatFullInr(item.total_amount)}>
                      {formatShortInr(item.total_amount)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="dashboard-empty-state">
                  <FontAwesomeIcon icon={faTrophy} className="dashboard-empty-state__icon" />
                  <p className="mb-0">No company totals available yet.</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4 mb-4">
        <Col xs={12}>
          <QuotesTrendChart />
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <PageQuoteTable compact />
        </Col>
      </Row>
    </div>
  );
};

export default function DashboardOverview() {
  const { admin } = readAuthSession();
  const isSuperAdmin = (admin?.role_name || admin?.role?.name) === "Super Admin";
  if (isSuperAdmin) return <SuperAdminDashboard />;
  if (!hasUserPermission("quotes", "view", admin)) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-header">
          <div>
            <h2 className="dashboard-title mb-1">Dashboard</h2>
            <p className="dashboard-subtitle mb-0">Your account does not have access to quote reports.</p>
          </div>
          <CreateQuoteAction />
        </div>
      </div>
    );
  }
  return <VendorDashboard />;
}
